// src/client/hooks/useList.ts (веб)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import {
  GroupedProduct,
  PromoInterface,
  SaleItemInterface,
} from "../../../../common/types/types";
import { delayAction } from "../../../../common/utils/functions/delayAction";
import { globalSync } from "../../../../hoc/useRootLayout";
import { groupProducts } from "../../../../common/utils/functions/groupProducts";
import { ROUTES } from "../../../../common/utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectCurrentCluster } from "../../../../store/slices/currentClusterSlice";
import { selectForceStores } from "../../../../store/slices/storeListSlice";
import { checkItemAvailability } from "../../../../common/utils/functions/checkItemAvailability";
import { useSearch } from "../../../../common/hooks/useSearch";
import { getApi } from "../../../../api/api";
import {
  selectSaleItemList,
  upsertItems,
} from "../../../../store/slices/saleItemListSlice";

export const useList = (
  selectedCategory?: number,
  setSelectedCategory?: (id: number | undefined) => void,
) => {
  const list = useSelector(selectSaleItemList);
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get("category");
  const effectiveCategory = categoryFromUrl
    ? Number(categoryFromUrl)
    : selectedCategory;

  const [groupedList, setGroupedList] = useState<
    (SaleItemInterface | GroupedProduct)[]
  >([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();

  const currentCluster = useSelector(selectCurrentCluster, shallowEqual);
  const selectedStoreIds = useSelector(selectForceStores, shallowEqual);
  const forceStore = currentCluster?.forceStore ?? false;
  const dispatch = useDispatch();

  const loadFirstPage = useCallback(async () => {
    try {
      const api = getApi();
      const response = await api.getSaleItemList({ limit: 100, offset: 0 });
      if (response?.data?.length) {
        dispatch(upsertItems(response.data));
        setHasMore(
          response.data.length === 100 && response.data.length < response.total,
        );
        setTotalItems(response.total);
      }
    } catch (error) {
      console.error("Failed to load first page:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (list.length === 0) {
      loadFirstPage();
    }
  }, [loadFirstPage, list.length]);

  const remoteSearch = useCallback(
    async (query: string) => {
      try {
        const api = getApi();
        const response = await api.getSaleItemList({
          search: query,
          limit: 50,
          offset: 0,
        });
        setHasMore(false);

        // ✅ ДОБАВЛЕНО: сохраняем найденные товары в Redux
        if (response?.data?.length) {
          dispatch(upsertItems(response.data));
        }

        return response?.data || [];
      } catch (error) {
        console.error("Remote search error:", error);
        return [];
      }
    },
    [dispatch], // ✅ dispatch в зависимостях
  );

  const {
    query,
    setQuery,
    filteredItems: searchResults,
    loading: searchLoading,
  } = useSearch(list, remoteSearch);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || query) return;

    setLoadingMore(true);
    try {
      const api = getApi();
      const offset = list.length;
      const response = await api.getSaleItemList({
        limit: 50,
        offset,
        ...(effectiveCategory && { categoryId: effectiveCategory }),
      });

      if (response?.data?.length) {
        dispatch(upsertItems(response.data));
        setHasMore(response.data.length === 50);
        setTotalItems(response.total);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Load more error:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [list, loadingMore, hasMore, query, effectiveCategory, dispatch]);

  useEffect(() => {
    const sourceItems = searchResults.length > 0 ? searchResults : list;
    if (sourceItems?.length > 0) {
      const validItems = sourceItems.filter(
        (item) => item?.id != null && item?.name != null,
      );
      const grouped = groupProducts(validItems) || [];
      setGroupedList(grouped.filter((item) => item !== null));
    } else {
      setGroupedList([]);
    }
  }, [list, searchResults]);

  const filteredList = useMemo(() => {
    let result = groupedList;

    if (effectiveCategory) {
      result = result.filter((item) => {
        if (!item) return false;
        const categoryId =
          "type" in item && item.type === "group"
            ? Number(item.categoryId)
            : Number((item as SaleItemInterface).categoryId);
        return categoryId === effectiveCategory;
      });
    }

    if (!currentCluster?.showOutOfStock) {
      result = result.filter((item) => {
        if ("type" in item && item.type === "group") {
          const group = item as GroupedProduct;
          return group.variants.some(
            (variant) =>
              checkItemAvailability({
                item: variant,
                forceStore,
                selectedStoreIds,
              }).available,
          );
        }
        const saleItem = item as SaleItemInterface;
        return checkItemAvailability({
          item: saleItem,
          forceStore,
          selectedStoreIds,
        }).available;
      });
    }

    return result;
  }, [groupedList, effectiveCategory, forceStore, selectedStoreIds]);

  const getList = useCallback(async () => {
    try {
      setRefreshing(true);
      await delayAction(1500);
      if (globalSync.refreshCatalog) {
        await globalSync.refreshCatalog();
      }
      setRefreshing(false);
    } catch {
      setRefreshing(false);
    }
  }, []);

  const handleSearchChange = (value: string) => {
    setQuery(value);
    if (!value) {
      setSelectedCategory?.(undefined);
    }
  };

  const handleSearchClear = () => {
    setQuery("");
    setSelectedCategory?.(undefined);
  };

  const handleCategorySelect = useCallback(
    async (id: number | undefined) => {
      setSelectedCategory?.(id);

      if (id) {
        try {
          const api = getApi();
          const response = await api.getSaleItemList({
            limit: 50,
            offset: 0,
            categoryId: id,
          });
          if (response?.data?.length) {
            dispatch(upsertItems(response.data));
            setHasMore(response.data.length === 50);
            setTotalItems(response.total);
          }
        } catch (error) {
          console.error("Failed to load category items:", error);
        }
      } else {
        loadFirstPage();
      }
    },
    [setSelectedCategory, dispatch, loadFirstPage],
  );

  const handlePromoPress = (promo: PromoInterface) => {
    navigate(`${ROUTES.PROMO_ITEM}/${promo.id}`);
  };

  return {
    filteredList,
    refreshing,
    loadingMore,
    hasMore,
    totalItems,
    getList,
    loadMore,
    handlePromoPress,
    searchQuery: query,
    searchLoading,
    effectiveCategory,
    handleSearchChange,
    handleSearchClear,
    handleCategorySelect,
  };
};
